import AppConstants from "../../constants/AppConstants";
import { getTargetArea, getDropIndexOffset, getAreaOnlyOffset } from "@/utils/DnDUtils";
import { TOP } from "../../constants/DnDConstants";
import { DROP_AREA_CENTER, DROP_AREA_TOP } from "@/constants/LayerConstants";

const { ITEM_TYPE_ELEMENT, ITEM_TYPE_CANVAS_SET_BLOCK, ITEM_TYPE_PAGE_FOLDER, ITEM_TYPE_GROUP } =
  AppConstants;

/**
 * Initializes a drop callback for site panel.
 *
 * @param {Object} props - The parameters for the drop handler.
 * @param {string} props.id - The identifier for the drop target.
 * @param {number} props.index - The index of the drop target.
 * @param {number} props.listIndex - The list index of the drop target.
 * @param {HTMLElement} props.dropRef - The reference to the drop target element.
 * @param {string} props.type - The type of the drop target.
 * @param {boolean} props.inFolder - Indicates if the drop target is within a folder.
 * @param {string} props.dropIn - The drop-in type for the target.
 * @returns {Function} A function that handles the drop event.
 */

// todo: type these
export const sitePanelDrop =
  ({ id, index, listIndex, dropRef, type, inFolder, dropIn }) =>
  (dragItem, monitor) => {
    const area = getTargetArea(monitor, dropRef);

    // We don't pass dragIndex/dropIndex because _moveItems handles the up/down movement adjustment internally.
    const itemOffset = getAreaOnlyOffset(area);

    return {
      item: { ...dragItem, targetIndex: index + itemOffset },
      target: {
        id,
        type,
        inFolder,
        index: listIndex,
        dropIn,
        bottomDrop: type === ITEM_TYPE_PAGE_FOLDER && area === "bottom" && !dropIn,
      },
    };
  };

/**
 *
 * @param {*} param0
 * @returns
 */
export const folderHeaderDrop =
  ({ id, index: dropIndex }) =>
  (dragItem) => {
    return {
      item: { ...dragItem, targetIndex: dragItem.index > dropIndex ? dropIndex + 1 : dropIndex },
      target: { id, index: dropIndex, type: ITEM_TYPE_PAGE_FOLDER, dropIn: true },
    };
  };

export const pagePanelDrop =
  /**
   * Returns an initialized drop callback for page panels.
   * @param {Object} scopedProps
   * @param {("bottom" | "top" | "center")=} scopedProps.areaOverride
   * @param {string} scopedProps.id
   * @param {HTMLElement} scopedProps.dropRef
   * @param {number} scopedProps.index
   * @param {string=} scopedProps.canvasSet
   * @param {string} scopedProps.layerType
   * @param {string=} scopedProps.containedBy
   * @param {boolean=} scopedProps.allowCenterDrop
   * @param {boolean=} scopedProps.isGroupOpen
   * @returns
   */


    ({
      areaOverride,
      canvasSet,
      id,
      dropRef,
      index,
      layerType,
      containedBy,
      allowCenterDrop = false,
      isGroupOpen = false,
    }) =>
    /**
     *
     * @param {{
     *  id: string,
     *  index: number,
     *  layerType: string,
     * }} dragItem
     * @param {import("react-dnd/dist").DragLayerMonitor} monitor
     * @returns
     */
    (dragItem, monitor) => {
      const area = areaOverride || getTargetArea(monitor, dropRef, allowCenterDrop);
      const { index: dragIndex, layerType: itemLayerType } = dragItem;
      const isElement = itemLayerType === ITEM_TYPE_ELEMENT;
      const isCanvasSetBlock = itemLayerType === ITEM_TYPE_CANVAS_SET_BLOCK;
      let targetIndex = index;

      // elements are displayed in reverse order and element are handled differently due to multi-select
      if (isElement && area === TOP) {
        targetIndex += 1;
      } else if (!isElement && !isCanvasSetBlock) {
        targetIndex += getDropIndexOffset({ area, dragIndex, dropIndex: index });
      }

      const containedByNotNeeded =
        layerType === ITEM_TYPE_GROUP &&
        ((!isGroupOpen && area !== DROP_AREA_CENTER) || (isGroupOpen && area === DROP_AREA_TOP));
      const filteredContainedBy = containedByNotNeeded ? undefined : containedBy;

      const data = {
        item: dragItem,
        target: {
          canvasSet,
          id,
          index: targetIndex,
          layerType,
          dropArea: area,
          containedBy: filteredContainedBy,
        },
      };

      return { area, data };
    };

export const blockStateMarkerDrop =
  ({ id, index }) =>
  (dragItem) => ({
    data: {
      item: dragItem,
      target: { id, index, layerType: ITEM_TYPE_ELEMENT },
    },
  });
